import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORIES = [
  "Todos",
  "IA y futuro del trabajo",
  "Empresa y estrategia",
  "Fundraising y narrativa",
  "Geopolítica empresarial",
  "Liderazgo y construcción",
  "Notas breves",
];

const CATEGORY_BADGE = {
  "IA y futuro del trabajo": "badge-ia",
  "Empresa y estrategia": "badge-empresa",
  "Fundraising y narrativa": "badge-fundraising",
  "Geopolítica empresarial": "badge-geopolitica",
  "Liderazgo y construcción": "badge-liderazgo",
  "Notas breves": "badge-notas",
};

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-ES", { month: "long", year: "numeric" });
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [activeCategory, setActiveCategory] = useState("Todos");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = activeCategory !== "Todos" ? `?category=${encodeURIComponent(activeCategory)}` : "";
    axios.get(`${API}/posts${params}`)
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  return (
    <div className="pt-16 md:pt-20">
      {/* Header */}
      <section data-testid="blog-header" className="py-20 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="label-mono text-muted-foreground mb-4">Escritura</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-normal leading-[1.1] mb-6 max-w-xl">
            Ideas sobre empresa, tecnología y poder.
          </h1>
          <p className="text-muted-foreground max-w-lg leading-relaxed">
            Ensayos breves con tesis propias. Sin ruido, sin relleno, sin recetas universales.
            Escritos desde la experiencia de construir una compañía en un mercado complejo.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b border-border bg-card sticky top-16 md:top-20 z-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 overflow-x-auto">
          <div className="flex gap-3 min-w-max">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                data-testid={`category-filter-${cat}`}
                onClick={() => setActiveCategory(cat)}
                className={`label-mono px-4 py-2 transition-colors whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div key={n} className="bg-card h-64 animate-pulse" />
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div data-testid="no-posts" className="text-center py-24 text-muted-foreground">
              <p className="font-serif text-2xl mb-3">No hay artículos en esta categoría.</p>
              <button onClick={() => setActiveCategory("Todos")} className="label-mono text-primary hover:underline mt-2">
                Ver todos →
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/ideas/${post.slug}`}
                  data-testid={`blog-post-${post.id}`}
                  className="bg-card p-8 flex flex-col hover:-translate-y-0.5 hover:shadow-sm transition-all duration-300 group"
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className={`label-mono text-xs px-2 py-1 ${CATEGORY_BADGE[post.category] || "badge-notas"}`}>
                      {post.category}
                    </span>
                    <span className="label-mono text-muted-foreground">{post.read_time} min</span>
                  </div>
                  <p className="font-serif text-xl leading-snug mb-3 group-hover:text-primary transition-colors line-clamp-3 flex-1">
                    {post.title}
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3 mb-6">{post.excerpt}</p>
                  <div className="flex items-center justify-between mt-auto pt-4 border-t border-border">
                    <span className="label-mono text-muted-foreground text-xs">{formatDate(post.created_at)}</span>
                    <ChevronRight size={14} className="text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
